import type { LiveChatVisitor, StaffChatMessage } from '@nucleus-commerce/api-client/admin';

/** The inbox row patch on the store channel; names and previews come from a refetch. */
export interface ConversationEvent {
  id: string;
  storeId: string;
  number: number;
  kind: string;
  status: 'open' | 'pending' | 'closed';
  /** The shopper's visitor (LC7a), so the visitor list can move them into "chatting" at once. */
  visitorId?: string | null;
  assignedTo: string | null;
  departmentId: string | null;
  tags: string[];
  priority: number;
  staffUnreadCount: number;
  visitorUnreadCount: number;
  missed: boolean;
  lastMessageAt: string;
  closedAt: string | null;
  closeReason: string | null;
  trashed?: boolean;
}

export interface TypingFrame {
  conversationId: string;
  authorType: 'visitor' | 'staff';
  userId?: string;
  agent?: { displayName: string } | null;
  internal?: boolean;
  draft?: string | null;
}

export interface ReadFrame {
  conversationId: string;
  by: 'visitor' | 'staff';
  upToMessageId: string;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export function conversationOf(data: unknown): ConversationEvent | null {
  if (!isObject(data) || !isObject(data.conversation)) return null;
  const conversation = data.conversation;
  return typeof conversation.id === 'string' && typeof conversation.storeId === 'string'
    ? (conversation as unknown as ConversationEvent)
    : null;
}

export function messageOf(data: unknown): StaffChatMessage | null {
  if (!isObject(data) || !isObject(data.message)) return null;
  return typeof data.message.id === 'string' && typeof data.message.conversationId === 'string'
    ? (data.message as unknown as StaffChatMessage)
    : null;
}

export function typingOf(data: unknown): TypingFrame | null {
  return isObject(data) && typeof data.conversationId === 'string'
    ? (data as unknown as TypingFrame)
    : null;
}

export function readOf(data: unknown): ReadFrame | null {
  return isObject(data) &&
    typeof data.conversationId === 'string' &&
    typeof data.upToMessageId === 'string'
    ? (data as unknown as ReadFrame)
    : null;
}

/** `visitor.updated` (LCW1): the store and the whole live visitor row, at most once every 10 s per visitor. */
export interface VisitorFrame {
  storeId: string;
  visitor: LiveChatVisitor;
}

export function visitorOf(data: unknown): VisitorFrame | null {
  if (!isObject(data) || typeof data.storeId !== 'string' || !isObject(data.visitor)) return null;
  const visitor = data.visitor;
  if (typeof visitor.id !== 'string' || typeof visitor.state !== 'string') return null;
  // The row carries its store too; the frame's is the one the channel was published on.
  return {
    storeId: data.storeId,
    visitor: { ...(visitor as unknown as LiveChatVisitor), storeId: data.storeId },
  };
}

export function storeIdOf(data: unknown): string | null {
  return isObject(data) && typeof data.storeId === 'string' ? data.storeId : null;
}

export function conversationIdOf(data: unknown): string | null {
  return isObject(data) && typeof data.conversationId === 'string' ? data.conversationId : null;
}
